/**
 * Replie les ligatures françaises courantes (« œ », « æ ») vers leur forme
 * sans ligature (« oe », « ae ») avant indexation ou requête FTS5 (US-04).
 *
 * Le tokenizer `unicode61 remove_diacritics 2` posé sur `recipe_fts`
 * (001_initial.sql) retire les accents, mais ne réduit pas « œ » à « oe »
 * dans la version de SQLite embarquée par better-sqlite3 (vérifié
 * empiriquement : `SELECT ... MATCH 'oeuf'` ne trouve pas un texte contenant
 * « œuf » sans ce repli). Même problème que documenté dans
 * `normalizeIngredientName.ts` : ce n'est pas un accent, donc traité à part.
 * Appliqué symétriquement au texte indexé (`rebuildRecipeSearchIndex.ts`) et
 * à la requête tapée par l'utilisateur (`searchRecipes.ts`), sinon seul un
 * des deux côtés serait replié et la recherche resterait sourde à l'un des
 * deux sens (« oeuf » -> « œuf » en base, ou l'inverse).
 *
 * **Remonté dans `shared/` en US-73.** Le prédicat de collection
 * (`collectionMatch.ts`) rejoue en JavaScript ce que la recherche fait en SQL,
 * et il doit replier **exactement** comme elle : deux replis voisins mais
 * distincts produiraient une ligne « Rejoindra » qui annonce une collection
 * que la recherche ne ramènera pas. Le fichier du serveur reste, et réexporte.
 */
export function foldLigatures(text: string): string {
  return text.replace(/œ/gi, 'oe').replace(/æ/gi, 'ae');
}

/**
 * Clé de comparaison d'un libellé : ordre alphabétique de la bibliothèque
 * (US-22), égalité de nom entre deux étiquettes ou deux collections (US-21),
 * et comparaison des noms d'étiquettes dans les filtres (`searchRecipes.ts`,
 * où elle est la fonction SQL `mlc_sort_key`).
 *
 * `COLLATE NOCASE` ne suffit pas : la collation intégrée de SQLite ne replie
 * que les lettres ASCII. « Éclair » se retrouve alors **après** « Zakouski »,
 * parce que la comparaison tombe sur les points de code.
 */
export function cleDeComparaison(text: string | null): string {
  if (text === null) return '';
  return foldLigatures(text)
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .trim();
}
